import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { httpsCallable } from 'firebase/functions';
import {
  LuChevronLeft, LuChevronRight, LuDroplet, LuFlaskConical, LuInfo, LuScissors,
  LuShield, LuSparkles, LuSun, LuTrendingUp, LuUpload, LuWaves,
} from 'react-icons/lu';
import { functions } from '../../firebase/Init';
import { COPY } from '../../contracts/copyMap';
import LogoSpinner from '../oracle/LogoSpinner';
import { adherenceBonus } from '../../contracts/routineApi';
import { formatScore10 } from '../create/format';
import { detectPair, frameTransform } from '../../lib/facePrep/alignToFrame';
import AurumButton from '../oracle/AurumButton';
import GhostButton from '../oracle/GhostButton';

// "Imagine my glowup" — a single portrait wiped between TODAY and IMAGINED,
// beside the reason the change is being claimed.
//
//   ┌──────────────────────┬───────────────────────────────┐
//   │  [TODAY | IMAGINED]  │  IF YOU STAY CONSISTENT       │
//   │   one image, one     │    8.9  ↑ +0.5                │
//   │   draggable divider  │  WHAT'S DRIVING THIS          │
//   │                      │    · one row per active       │
//   │                      │  [ generated, not photographed ]
//   └──────────────────────┴───────────────────────────────┘
//
// THE WAIT STATE IS A DRAWN SPINNER, NOT A GIF (DESIGN.md §16.2 vs the Glowup
// surface rule). §16.2's "no spinner" governs the Oracle submit flow, where the
// wait is the ritual. This is not that surface: generation here takes TENS OF
// SECONDS behind a one-per-day cap, and a lone [GENERATING] word gave no signal
// that anything was happening — the most common reading was that it had hung.
//
// The Glowup/Vault rule is the one that applies, and it asks for a drawn aurum
// hairline circle rather than a stock spinner gif. LogoSpinner is exactly that:
// the crest inside a rotating aurum arc, motion-safe-gated. The [GENERATING]
// caption stays, underneath it. The cross-fade when the image lands is
// unchanged, and the wipe still only appears once there are two images.
//
// ── NO CENSOR BARS IN HERE ──────────────────────────────────────────────────
//
// This used to carry a [SIMULATED] Censor Bar in the header and another over
// the simulated half. Both are gone. The disclosure did not weaken: the mark
// that does the work is BURNED INTO THE STORED JPEG by renderFunctions/
// watermark, so it survives a screenshot, a download and a re-share, and the
// disclaimer panel states it in words beside the image. Two hard black bars in
// compressed caps were a third copy of a claim already made twice, and they
// competed with the only thing this frame exists to show. The quiet TODAY /
// IMAGINED labels are wayfinding for the wipe, not the disclosure.
//
// Do not add a CSS-only [SIMULATED] back. If the burned watermark ever stops
// being applied, fix that — a CSS overlay is not a substitute for it.
//
// NO "GENERATE AGAIN". The cap is one render per day (generateGlowupImage
// MAX_RENDERS_PER_DAY), so a regenerate button would be a control that fails
// every time it is pressed. Reopening still re-reads the cache for free.
export default function GlowupRenderModal({
  open,
  beforeUrl,
  // 1-100, or null on a SEALED reading — listVaultEntries withholds the score
  // of a scan the user has not opened. Null hides the projection panel rather
  // than projecting from nothing.
  beforeScore = null,
  // Product actives from the live routine. Drive WHAT'S DRIVING THIS, and via
  // their stored `adherence`, the projection.
  actives = [],
  onClose,
}) {
  const [state, setState] = useState({ phase: 'idle' });
  // ── ONE GENERATION IN FLIGHT AT A TIME ──────────────────────────────────
  //
  // Generation takes tens of seconds and closing the modal does not cancel it.
  // Close-then-reopen used to fire a second callable — which, now that the cap
  // is one render per day, comes back RATE_LIMITED and tells the user they are
  // out of renderings while the first one is still on its way to succeeding.
  //
  // The server reserves the slot transactionally, so this is not the safety
  // boundary. It is here so the user is never shown a refusal that is about to
  // be contradicted.
  const inFlightRef = useRef(false);

  const generate = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setState({ phase: 'generating' });
    try {
      const call = httpsCallable(functions, 'generateGlowupImage');
      const res = await call({});
      const data = res?.data;
      if (data?.status === 'success') {
        setState({
          phase: 'ready', url: data.image_url,
          // The exact image the model edited. See generateGlowupImage.
          beforeUrl: data.before_url || null,
          cached: data.cached,
        });
        return;
      }
      setState({
        phase: 'error',
        message: COPY.GLOWUP_RENDER_ERRORS[data?.error_code]
          || COPY.GLOWUP_RENDER_ERROR_FALLBACK,
      });
    } catch (err) {
      console.error('GlowupRenderModal: generate failed', err);
      setState({ phase: 'error', message: COPY.GLOWUP_RENDER_ERROR_FALLBACK });
    } finally {
      inFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (open && state.phase === 'idle') generate();
  }, [open, state.phase, generate]);

  // Reset on close so reopening re-reads the cache rather than showing a stale
  // render from a routine the user has since changed — but NOT while a call is
  // in flight. Resetting to `idle` under a live request drops the modal back to
  // an unlabelled empty frame and arms the reopen effect to fire again.
  useEffect(() => {
    if (!open && !inFlightRef.current) setState({ phase: 'idle' });
  }, [open]);

  const projection = useMemo(() => projectScore(beforeScore, actives), [beforeScore, actives]);
  const ready = state.phase === 'ready' && !!state.url;

  // PREFER THE SERVER'S BEFORE. `before_url` is the exact file handed to the
  // image model; the `beforeUrl` prop is the Vault thumbnail, a 256px
  // derivative of a different step. The prop survives only as the pre-render
  // placeholder (there is no response yet) and as the fallback if signing the
  // source failed.
  const comparisonBefore = state.beforeUrl || beforeUrl;

  // Cross-origin signed Storage URLs ignore an anchor's `download`, so fetch the
  // bytes and hand the browser a blob. If CORS refuses, open the image instead —
  // [SIMULATED] is burned into the JPEG, so a manual save carries it too.
  const save = useCallback(async () => {
    if (!ready) return;
    try {
      const res = await fetch(state.url);
      if (!res.ok) throw new Error(`status ${res.status}`);
      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = 'mogged-simulated.jpg';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
    } catch (err) {
      console.warn('GlowupRenderModal: blob save failed, opening instead', err);
      window.open(state.url, '_blank', 'noopener,noreferrer');
    }
  }, [ready, state.url]);

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-start justify-center bg-obsidian/92 px-4 py-6 lg:px-8 overflow-y-auto"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3, ease: [0.65, 0, 0.35, 1] }}
        role="dialog"
        aria-modal="true"
        aria-label={COPY.GLOWUP_RENDER_CTA}
        data-testid="glowup-render"
      >
        <motion.div
          // BOUNDED TO THE VIEWPORT, and the BODY is what scrolls.
          //
          // The panel used to grow to its content and overflow: on a 1280×800
          // laptop it came out 871px tall, so the headline sat above the top of
          // the screen and SAVE / CLOSE were below the bottom of it. Capping the
          // image alone did not fix it — the right-hand column (projection +
          // one row per active + disclaimer) is what drives the height, and
          // that grows with the size of someone's regimen.
          //
          // So the panel is capped at the viewport minus the overlay's own
          // py-6, header and footer are pinned, and only the middle scrolls.
          // The controls are reachable at any window height with any number of
          // actives. dvh, not vh, so mobile browser chrome does not eat CLOSE.
          className="flex w-full max-w-5xl my-auto max-h-[calc(100dvh-3rem)] flex-col bg-ash border border-hairline-soft rounded-panel p-5 lg:p-6 xl:p-8"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        >
          {/* ── header ────────────────────────────────────────────────────── */}
          <header className="shrink-0 flex flex-col gap-3">
            <h2 className="font-display text-3xl lg:text-4xl text-bone m-0">
              {COPY.GLOWUP_RENDER_HEADLINE}
            </h2>
            <p className="font-display italic text-bone-dim text-sm max-w-lg m-0 leading-relaxed">
              {COPY.GLOWUP_RENDER_BODY}
            </p>
          </header>

          {/* ── body ──────────────────────────────────────────────────────── */}
          {/* The scroll lives on this WRAPPER, never on the grid itself. A grid
              with a definite block size compresses its auto rows to fit, and the
              image frame sizes from `aspect-ratio` — which has a min-content
              contribution of zero. Putting overflow on the grid collapsed its
              row to 2px and the portrait drew straight over the drivers list.
              With the wrapper constrained, the grid keeps its natural height
              and scrolls inside. */}
          <div className="mt-6 min-h-0 flex-1 overflow-y-auto">
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] lg:gap-7">
            {ready ? (
              <ComparisonSlider beforeUrl={comparisonBefore} afterUrl={state.url} />
            ) : (
              <SinglePane url={comparisonBefore} generating={state.phase === 'generating'} />
            )}

            <div className="flex flex-col gap-5">
              {projection && <ProjectionPanel {...projection} />}
              {actives.length > 0 && <Drivers actives={actives} />}
              <Disclaimer />
            </div>
          </div>
          </div>

          {/* Bone, never red — DESIGN.md forbids red even for errors. */}
          {state.phase === 'error' && (
            <p className="font-display text-bone text-sm mt-5 m-0" role="alert">
              {state.message}
            </p>
          )}

          {/* ── footer ────────────────────────────────────────────────────── */}
          <footer className="shrink-0 mt-6 pt-5 border-t border-hairline-soft flex flex-wrap items-center justify-between gap-3">
            <span className="font-display italic text-bone-dim text-sm">
              {ready ? COPY.GLOWUP_RENDER_DRAG_HINT : ' '}
            </span>
            <div className="flex items-center gap-3 ml-auto">
              {state.phase === 'error' && (
                <GhostButton onClick={generate} data-testid="render-retry">
                  {COPY.RETRY_CTA}
                </GhostButton>
              )}
              {ready && (
                <GhostButton onClick={save} data-testid="render-save">
                  <span className="inline-flex items-center gap-2">
                    <LuUpload size={14} strokeWidth={1.75} aria-hidden="true" />
                    {COPY.GLOWUP_RENDER_SAVE_CTA}
                  </span>
                </GhostButton>
              )}
              <AurumButton onClick={onClose} data-testid="render-close">
                {COPY.GLOWUP_RENDER_CLOSE_CTA}
              </AurumButton>
            </div>
          </footer>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ── the projection ──────────────────────────────────────────────────────────
//
// NOT A FORECAST. `adherenceBonus` is the published, capped delta the product
// already promises per active on the Routine page (ADHERENCE_META: +0.4 used
// consistently, +0.1 sometimes) and that fuseScores actually applies to the next
// reading. This shows the difference between what the user is reporting today
// and what full reported use would pay — which is a promise the backend keeps,
// not a prediction about a face.
//
// Returns null when there is nothing honest to show: no score (sealed reading),
// no actives, or a non-finite score.
export function projectScore(beforeScore, actives) {
  // `''` coerces to 0, not NaN, so it has to be rejected by identity before the
  // finite check — the same trap formatScore10 documents at length.
  if (beforeScore === null || beforeScore === undefined || beforeScore === '') return null;
  const today = Number(beforeScore);
  if (!Number.isFinite(today)) return null;
  const list = (Array.isArray(actives) ? actives : []).filter((a) => a && a.active_id);
  if (!list.length) return null;

  const byId = (pick) => {
    const out = {};
    for (const a of list) out[a.active_id] = pick(a);
    return out;
  };
  const current = adherenceBonus(byId((a) => a.adherence));
  const full = adherenceBonus(byId(() => 'yes'));
  const gain = Math.max(0, full - current); // 1-100 scale

  return {
    todayLabel: formatScore10(today),
    projectedLabel: formatScore10(Math.max(1, Math.min(100, today + gain))),
    // One decimal on the displayed 1-10 scale, matching formatScore10.
    gainLabel: gain > 0 ? `+${(gain / 10).toFixed(1)}` : null,
  };
}

const ProjectionPanel = ({ todayLabel, projectedLabel, gainLabel }) => (
  <section
    className="bg-[rgba(255,255,255,0.03)] border border-hairline-soft rounded-card px-6 py-5"
    data-testid="render-projection"
  >
    <h3 className="font-display uppercase text-[11px] tracking-[0.22em] text-bone-dim m-0">
      {COPY.GLOWUP_RENDER_PROJECTION_LABEL}
    </h3>
    <div className="flex items-baseline gap-3 mt-2">
      <span className="font-censor text-5xl text-bone-pure leading-none">{projectedLabel}</span>
      {/* Aurum, not green. DESIGN.md §2.1 keeps every metric value on the one
          accent; a second signal colour for "good" is not in the allow-list. */}
      {gainLabel && (
        <span className="inline-flex items-center gap-1 font-display text-lg text-aurum">
          <LuTrendingUp size={16} strokeWidth={2} aria-hidden="true" />
          {gainLabel}
        </span>
      )}
    </div>
    <p className="font-display text-sm text-bone-dim mt-2 m-0">
      {gainLabel
        ? COPY.GLOWUP_RENDER_PROJECTION_FROM(todayLabel)
        : COPY.GLOWUP_RENDER_PROJECTION_AT_CEILING}
    </p>
  </section>
);

// ── what's driving this ─────────────────────────────────────────────────────

// One glyph per regimen step. `step` is the only classification every active
// carries (assembleRoutine uppercases the catalog's cleanse/treat/hydrate/
// protect/tool), so it is what the icon keys off.
const STEP_ICONS = {
  CLEANSE: LuWaves,
  TREAT: LuFlaskConical,
  HYDRATE: LuDroplet,
  PROTECT: LuSun,
  TOOL: LuScissors,
  CONSULT: LuShield,
};

const Drivers = ({ actives }) => (
  <section data-testid="render-drivers">
    <h3 className="font-display uppercase text-[11px] tracking-[0.22em] text-bone-dim m-0">
      {COPY.GLOWUP_RENDER_DRIVERS_LABEL}
    </h3>
    <ul className="list-none p-0 m-0 mt-1">
      {actives.map((a) => {
        const Icon = STEP_ICONS[String(a.step || '').toUpperCase()] || LuSparkles;
        return (
          <li
            key={a.active_id}
            className="flex items-center gap-3 py-2.5 border-b border-hairline-soft last:border-b-0"
          >
            <span className="text-aurum shrink-0" aria-hidden="true">
              <Icon size={15} strokeWidth={1.5} />
            </span>
            <span className="font-display text-[15px] text-bone flex-1 min-w-0 truncate">
              {a.active}
            </span>
            {/* Already "SKIN · TEXTURE" — assembleRoutine builds `concern` from
                the catalog area, so no formatting is needed here. */}
            {a.concern && (
              <span className="font-display uppercase text-[10px] tracking-[0.16em] text-bone-dim shrink-0">
                {a.concern}
              </span>
            )}
          </li>
        );
      })}
    </ul>
  </section>
);

const Disclaimer = () => (
  <div className="flex items-start gap-3 px-5 py-4 bg-[rgba(255,255,255,0.03)] border border-hairline-soft rounded-field">
    <span className="text-bone-dim shrink-0 mt-px" aria-hidden="true">
      <LuInfo size={16} strokeWidth={1.5} />
    </span>
    <span className="font-display text-sm leading-relaxed text-bone-dim">
      {COPY.GLOWUP_RENDER_DISCLAIMER}
    </span>
  </div>
);

// ── the wipe ────────────────────────────────────────────────────────────────
//
// ONE frame, two stacked images. IMAGINED is the base layer; TODAY is clipped to
// the left of the divider, so dragging right reveals the simulation out of the
// real portrait. Side-by-side panes made the two faces hard to compare at this
// size — the whole claim is "the same face, changed", and a wipe is the only
// layout that can carry it.
//
// ── THE TWO FACES DO NOT LINE UP BY THEMSELVES ──────────────────────────────
//
// object-cover on both was wrong. The scan is a 3:4 crop; the model is asked
// for 1024×1536, which is 2:3, so it RECOMPOSES the frame and the face comes
// back at a different scale and height. Cover-cropping two differently-framed
// portraits slides one face past the other as the divider moves.
//
// So both images are aligned to the frame by their EYE LANDMARKS instead (see
// lib/facePrep/alignToFrame) — the same similarity transform the scan pipeline
// uses, applied as CSS. Whatever either image did with its framing stops
// mattering; the eyes land on the same point at the same interocular distance,
// and the wipe becomes a comparison rather than a slideshow.
//
// Alignment is BEST EFFORT. No landmarker, no detectable face, or a signed URL
// the browser will not let us read cross-origin, and `aligned` stays null and
// both images fall back to object-cover — exactly what shipped before. It has
// to degrade this way: a modal that renders nothing because a face model failed
// to load would be a far worse bug than two portraits that do not quite match.
//
// Pointer, not mouse: one handler set covers mouse, touch and pen. Keyboard
// works too — the divider is a real slider, arrows nudge, Home/End slam.
export function ComparisonSlider({ beforeUrl, afterUrl }) {
  const [pos, setPos] = useState(50);
  const [aligned, setAligned] = useState(null);
  const [frame, setFrame] = useState(null);
  const boxRef = useRef(null);
  const draggingRef = useRef(false);

  // Frame size drives the transform, so it is measured rather than assumed and
  // re-measured on resize — the modal is responsive and the box changes size.
  useEffect(() => {
    const el = boxRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const measure = () => {
      const r = el.getBoundingClientRect();
      if (r.width && r.height) setFrame({ w: r.width, h: r.height });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    let cancelled = false;
    setAligned(null);
    detectPair(beforeUrl, afterUrl).then((pair) => {
      if (!cancelled) setAligned(pair);
    });
    return () => { cancelled = true; };
  }, [beforeUrl, afterUrl]);

  // Both transforms or neither — see detectPair.
  const fit = aligned && frame
    ? {
      before: frameTransform(aligned.before, frame.w, frame.h),
      after: frameTransform(aligned.after, frame.w, frame.h),
    }
    : null;
  const layerStyle = (which) => (fit && fit[which]
    ? { position: 'absolute', top: 0, left: 0, transformOrigin: '0 0', ...fit[which] }
    : null);

  const setFromClientX = useCallback((clientX) => {
    const el = boxRef.current;
    if (!el || !Number.isFinite(clientX)) return;
    const rect = el.getBoundingClientRect();
    if (!rect.width) return; // pre-layout: leave the divider centred
    // Math.max/min PROPAGATE NaN rather than clamping it, so a bad clientX
    // would stick `left: NaN%` on the divider and freeze the wipe for good.
    // Guarded above, at the one place a number enters.
    setPos(Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100)));
  }, []);

  // MOVE FIRST, CAPTURE SECOND. setPointerCapture throws NotFoundError for a
  // pointerId that is not currently active, and an uncaught throw here would
  // abort the handler before the divider had moved at all — a wipe that ignores
  // the press that started it. Capture is an enhancement (it keeps the drag
  // alive once the cursor leaves the frame), never a precondition.
  const onPointerDown = (e) => {
    draggingRef.current = true;
    setFromClientX(e.clientX);
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (err) { /* no-op */ }
  };
  const onPointerMove = (e) => {
    if (draggingRef.current) setFromClientX(e.clientX);
  };
  const endDrag = (e) => {
    draggingRef.current = false;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (err) { /* no-op */ }
  };

  const onKeyDown = (e) => {
    const step = e.shiftKey ? 10 : 2;
    const next = { ArrowLeft: () => pos - step, ArrowRight: () => pos + step, Home: () => 0, End: () => 100 }[e.key];
    if (!next) return;
    e.preventDefault();
    setPos(Math.max(0, Math.min(100, next())));
  };

  return (
    <div
      ref={boxRef}
      // aspect-[3/4] fixes the RATIO; max-w caps the derived HEIGHT. Without
      // the cap a 5xl modal on a 1280×800 laptop made a ~670px-tall image, and
      // the header scrolled off the top of the viewport with the footer out of
      // reach below. Bounding the width by `56vh × 3/4` keeps the frame at most
      // 56vh tall whatever the column width, so the modal fits without scrolling
      // on a laptop and still fills a large screen.
      className="relative mx-auto aspect-[3/4] w-full max-w-[calc(56vh*0.75)] overflow-hidden rounded-panel bg-vein border border-hairline-soft select-none touch-none"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
    >
      {/* base layer — the simulation */}
      <div className="absolute inset-0 overflow-hidden" data-testid="render-after">
        <motion.img
          src={afterUrl}
          alt=""
          aria-hidden="true"
          draggable={false}
          className={layerStyle('after') ? 'max-w-none' : 'w-full h-full object-cover'}
          style={layerStyle('after') || undefined}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        />
      </div>

      {/* clipped layer — today, revealed to the LEFT of the divider */}
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
        data-testid="render-before"
      >
        {beforeUrl && (
          <img
            src={beforeUrl}
            alt=""
            aria-hidden="true"
            draggable={false}
            className={layerStyle('before') ? 'max-w-none' : 'w-full h-full object-cover'}
            style={layerStyle('before') || undefined}
          />
        )}
      </div>

      {/* divider + handle */}
      <div
        className="absolute inset-y-0 w-px bg-aurum pointer-events-none"
        style={{ left: `${pos}%` }}
        aria-hidden="true"
      />
      <div
        role="slider"
        tabIndex={0}
        aria-label="Compare today with the simulated result"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(pos)}
        onKeyDown={onKeyDown}
        data-testid="render-divider"
        className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-obsidian/80 border border-aurum flex items-center justify-center cursor-ew-resize text-aurum focus:outline-none focus-visible:ring-1 focus-visible:ring-aurum"
        style={{ left: `${pos}%` }}
      >
        <LuChevronLeft size={13} strokeWidth={2} aria-hidden="true" />
        <LuChevronRight size={13} strokeWidth={2} aria-hidden="true" />
      </div>

      {/* Both sides get the SAME quiet label. The Censor Bar that used to mark
          the simulated half is gone: the [SIMULATED] mark that actually matters
          is burned into the stored JPEG by renderFunctions/watermark, so it
          survives a screenshot, and the disclaimer panel states it in words. A
          third copy in hard black type was shouting a claim already made twice,
          and it fought the one thing this frame is for — seeing the face. */}
      <PaneLabel className="left-4 bottom-4">{COPY.GLOWUP_RENDER_BEFORE_LABEL}</PaneLabel>
      <PaneLabel className="right-4 bottom-4">{COPY.GLOWUP_RENDER_AFTER_LABEL}</PaneLabel>
    </div>
  );
}

// Pre-render frame: the portrait the user already has, dimmed, with the drawn
// spinner and its [GENERATING] caption over it. See the header for which design
// rule applies here and why it is not §16.2's.
const SinglePane = ({ url, generating }) => (
  <div
    // Same height cap as the wipe, so the layout does not jump when the render
    // lands and this frame is replaced by ComparisonSlider.
    className="relative mx-auto aspect-[3/4] w-full max-w-[calc(56vh*0.75)] overflow-hidden rounded-panel bg-vein border border-hairline-soft"
    data-testid="render-before"
  >
    {url && (
      <img
        src={url}
        alt=""
        aria-hidden="true"
        className={`w-full h-full object-cover ${generating ? 'opacity-40' : ''}`}
      />
    )}
    {generating && (
      <div className="absolute inset-0 flex items-center justify-center">
        {/* The caption is LogoSpinner's own label slot rather than a sibling
            span, so the spinner's role="status" announces the wait once
            instead of a screen reader meeting a bare decorative graphic. */}
        <LogoSpinner size={84} label={COPY.GLOWUP_RENDER_GENERATING} />
      </div>
    )}
    <PaneLabel className="left-4 bottom-4">{COPY.GLOWUP_RENDER_BEFORE_LABEL}</PaneLabel>
  </div>
);

const PaneLabel = ({ children, className = '' }) => (
  <span
    className={`absolute font-display uppercase text-[10px] tracking-[0.2em] text-bone bg-obsidian/70 px-3 py-1.5 rounded-field ${className}`}
  >
    {children}
  </span>
);
